import { useMemo } from 'react';

import clsx from 'clsx';
import Imgix from 'react-imgix';
import ReactPlayer from 'react-player';

import { useWindowSize } from '@website/hooks/useWindowsSize';
import { TripsPageQuery } from '@website/types/graphqlOperations';
import { getImageUrl } from '../../media/utils/imgix';
import styles from './TripImages.module.scss';
import useTripsDiscountRate from './useTripsDiscountRate';

type TripImagesProps = {
  trip: TripsPageQuery['tripByURL'];
};

export default function TripImages({ trip }: TripImagesProps) {
  const size = useWindowSize();

  const isMobile = size.width < 576;

  const featuredImage = useMemo(
    () => trip.featuredImages?.[0] ?? trip.image,
    [trip.featuredImages, trip.image],
  );

  const discountRate = useTripsDiscountRate(trip);

  const showVideoLayout =
    !isMobile &&
    trip.tripVideoURL &&
    trip.featuredImages &&
    trip.featuredImages.length > 1;
  const showGalleryLayout =
    !isMobile &&
    !showVideoLayout &&
    trip.featuredImages &&
    trip.featuredImages.length > 2;
  const showFallbackLayout = !showVideoLayout && !showGalleryLayout;

  // Prepare image arrays safely
  const videoLayoutImages =
    showVideoLayout && trip.featuredImages
      ? trip.featuredImages.slice(0, 2)
      : [];
  const galleryLayoutMainImage =
    showGalleryLayout && trip.featuredImages ? trip.featuredImages[0] : null;
  const galleryLayoutThumbnails =
    showGalleryLayout && trip.featuredImages
      ? trip.featuredImages.slice(1, 3)
      : [];

  return (
    <div className={styles.tripImagesRootContainer}>
      {/* Video Layout: Visible if showVideoLayout is true */}
      {trip.tripVideoURL && trip.featuredImages && (
        <div
          className={clsx(styles.featuredContainer, styles.videoLayout, {
            [styles.active]: showVideoLayout,
          })}
        >
          <div className={styles.featuredVideo}>
            <ReactPlayer
              height="100%"
              width="100%"
              url={trip.tripVideoURL}
              controls
              playing
              playIcon={<></>}
              light={
                trip.tripVideoURLThumbnail?.file?.key
                  ? getImageUrl(trip.tripVideoURLThumbnail.file.key)
                  : true
              }
            />
          </div>
          {videoLayoutImages.map((image) => (
            <div key={image?.file?.key} className={styles.featuredImages}>
              <Imgix
                src={getImageUrl(image?.file?.key)}
                srcSetOptions={{
                  widths: [400, 600, 800],
                  devicePixelRatios: [1, 2],
                  maxWidth: 800,
                  minWidth: 400,
                }}
                htmlAttributes={{
                  style: {
                    aspectRatio: '16/9',
                  },
                  alt: trip.title,
                }}
                imgixParams={{
                  ar: '16:9',
                  auto: 'compress,format,enhance,redeye',
                  crop: 'faces,edges',
                  fit: 'crop',
                }}
                className="aspect-ratio aspect-ratio-16x9 w-100"
              />
            </div>
          ))}
        </div>
      )}

      {/* Gallery Layout: Visible if showGalleryLayout is true */}
      {galleryLayoutMainImage && trip.featuredImages && (
        <div
          className={clsx(styles.featuredContainer, styles.galleryLayout, {
            [styles.active]: showGalleryLayout,
          })}
        >
          <div className={styles.featuredVideo}>
            {' '}
            {/* Assuming .featuredVideo class styles the large item container */}
            <Imgix
              key={galleryLayoutMainImage?.file?.key}
              src={getImageUrl(galleryLayoutMainImage?.file?.key)}
              srcSetOptions={{
                widths: [400, 600, 800, 1200],
                devicePixelRatios: [1, 2],
                maxWidth: 1600,
                minWidth: 400,
              }}
              htmlAttributes={{
                style: {
                  aspectRatio: '16/9',
                },
                alt: trip.title,
              }}
              imgixParams={{
                ar: '16:9',
                auto: 'compress,format,enhance,redeye',
                crop: 'faces',
                fit: 'crop',
              }}
              className="w-100 h-100"
            />
          </div>
          {galleryLayoutThumbnails.map((img) => (
            <div key={img?.file?.key} className={styles.featuredImages}>
              <Imgix
                src={getImageUrl(img?.file?.key)}
                srcSetOptions={{
                  widths: [400, 600, 800],
                  devicePixelRatios: [1, 2],
                  maxWidth: 1600,
                  minWidth: 400,
                }}
                htmlAttributes={{
                  style: {
                    aspectRatio: '16/9',
                  },
                  alt: trip.title,
                }}
                imgixParams={{
                  ar: '16:9',
                  auto: 'compress,format,enhance,redeye',
                  crop: 'faces,entropy',
                  fit: 'crop',
                }}
                className={clsx('aspect-ratio aspect-ratio-16x9 w-100')}
              />
            </div>
          ))}
        </div>
      )}

      {/* Fallback Layout: Visible if showFallbackLayout is true */}
      <div
        className={clsx(styles.fallbackSection, {
          // Uses existing fallbackSection class
          [styles.active]: showFallbackLayout,
        })}
      >
        {(trip.overlayText || trip.minimumPrice?.priceBeforeDiscount) && (
          <div className={`${styles.ribbon}`}>
            <span>
              {discountRate
                ? `ON SALE: Save ${discountRate}%`
                : trip.overlayText}
            </span>
          </div>
        )}
        {/* Show 1:1 aspect ratio for mobile (bootstrap xs) and 4:1 for larger screens (sm+)  */}
        {/* Assuming styles.mobileFeaturedImages and styles.desktopFeaturedImages handle their own visibility via media queries */}
        {featuredImage?.file?.key && (
          <>
            <div className={styles.mobileFeaturedImages}>
              <Imgix
                imgixParams={{
                  auto: 'compress,format,enhance,redeye',
                  crop: 'faces',
                  fit: 'crop',
                  ar: '1:1',
                }}
                src={`${getImageUrl(featuredImage.file.key)}`}
                sizes="100vw"
                className="w-100 h-100"
                htmlAttributes={{
                  alt: trip.title,
                }}
              />
            </div>
            <div className={styles.desktopFeaturedImages}>
              <Imgix
                imgixParams={{
                  auto: 'compress,format,enhance,redeye',
                  crop: 'faces',
                  fit: 'crop',
                  // Original desktop fallback didn't specify 'ar', respecting that.
                }}
                src={`${getImageUrl(featuredImage.file.key)}`}
                sizes="100vw"
                className="w-100 h-100"
                htmlAttributes={{
                  alt: trip.title,
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
